<template>
  <div class="desktop-container" :class="{ 'desktop-mode': isDesktopMode }">
    <!-- Desktop Icons -->
    <div class="desktop-icons" @dragover="onDragOver" @drop="onDrop">
      <div v-for="(item, index) in visibleDesktopItems" :key="index"
           class="desktop-icon"
           :style="{
             transform: `translate(${iconPositions[item.id]?.x || 0}px, ${iconPositions[item.id]?.y || 0}px)`
           }"
           draggable="true"
           @dragstart="startIconDrag($event, item)"
           @click="openWindow(item)">
        <v-icon size="48" :color="item.color">{{ item.icon }}</v-icon>
        <span class="icon-label">{{ item.label }}</span>
      </div>

      <!-- Papierkorb -->
      <div class="recycle-bin"
           :style="{
             transform: `translate(${recycleBin.position.x}px, ${recycleBin.position.y}px)`
           }"
           @dragover="onDragOver"
           @drop="onDrop">
        <v-icon size="48" color="error">{{ recycleBin.icon }}</v-icon>
        <span class="icon-label">{{ recycleBin.label }}</span>
        <!-- Recycle Bin Menu -->
        <v-menu v-if="recycleBin.items.size > 0" location="top">
          <template v-slot:activator="{ props }">
            <div v-bind="props" class="recycle-bin-count">
              {{ recycleBin.items.size }}
            </div>
          </template>
          <v-card class="recycle-bin-menu">
            <v-list>
              <v-list-item
                v-for="iconId in Array.from(recycleBin.items)"
                :key="iconId"
                @click="restoreIcon(iconId)"
                class="recycle-bin-item"
              >
                <v-icon :color="getIconColor(iconId)">
                  {{ getIconName(iconId) }}
                </v-icon>
                <span class="ml-2">{{ getIconLabel(iconId) }}</span>
              </v-list-item>
            </v-list>
          </v-card>
        </v-menu>
      </div>
    </div>

    <!-- Windows -->
    <div v-for="window in windows" :key="window.id"
         class="window"
         :class="{
           'minimized': window.isMinimized,
           'dragging': window.isDragging,
           'resizing': window.isResizing
         }"
         :style="window.style"
         @mousedown="bringToFront(window.id)">
      <div class="window-header" @mousedown="startWindowDrag($event, window.id)">
        <div class="window-title">
          <v-icon size="20" class="mr-2" :color="window.iconColor">{{ window.icon }}</v-icon>
          {{ window.title }}
        </div>
        <div class="window-controls">
          <v-btn icon size="x-small" variant="text" @click.stop="minimizeWindow(window.id)">
            <v-icon>mdi-minus</v-icon>
          </v-btn>
          <v-btn icon size="x-small" variant="text" @click.stop="maximizeWindow(window.id)">
            <v-icon>{{ window.isMaximized ? 'mdi-window-restore' : 'mdi-window-maximize' }}</v-icon>
          </v-btn>
          <v-btn icon size="x-small" variant="text" color="error" @click.stop="closeWindow(window.id)">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>
      </div>
      <div class="window-content" @mousedown.stop>
        <component
          :is="componentMap[window.id]"
          v-if="!window.isMinimized && componentMap[window.id]"
          class="window-component"
        />
      </div>
      <!-- Resize handles -->
      <div class="resize-handle resize-right" @mousedown.stop="startResize($event, window.id, 'right')"></div>
      <div class="resize-handle resize-bottom" @mousedown.stop="startResize($event, window.id, 'bottom')"></div>
      <div class="resize-handle resize-corner" @mousedown.stop="startResize($event, window.id, 'corner')"></div>
    </div>

    <!-- Taskbar -->
    <div class="taskbar">
      <div class="start-menu" @click="toggleStartMenu">
        <v-icon color="success">mdi-menu</v-icon>
      </div>

      <div class="taskbar-items">
        <div v-for="window in windows" :key="window.id"
             class="taskbar-item"
             :class="{ 'active': window.isActive && !window.isMinimized }"
             @click="toggleWindow(window.id)">
          <v-icon size="20" :color="window.iconColor">{{ window.icon }}</v-icon>
          <span class="taskbar-label">{{ window.title }}</span>
        </div>
      </div>

      <div class="system-tray">
        <div class="tray-item">
          <v-icon size="20">mdi-wifi</v-icon>
        </div>
        <div class="tray-item">
          <v-icon size="20">mdi-volume-high</v-icon>
        </div>
        <div class="tray-item">
          <v-icon size="20">mdi-battery</v-icon>
        </div>
        <div class="tray-item time" @click="toggleCalendar" @mouseenter="showDate = true" @mouseleave="showDate = false">
          <span v-if="!showDate">{{ currentTime }}</span>
          <span v-else>{{ currentDate }}</span>
          <v-menu
            v-model="showCalendar"
            :close-on-content-click="false"
            location="top"
            offset="10"
          >
            <template v-slot:activator="{ props }">
              <div v-bind="props"></div>
            </template>
            <v-card class="calendar-popup">
              <v-date-picker v-model="selectedDate" :show-adjacent-months="true" color="success"></v-date-picker>
            </v-card>
          </v-menu>
        </div>
      </div>
    </div>

    <!-- Start Menu -->
    <div v-if="showStartMenu" class="start-menu-panel" @click.self="showStartMenu = false">
      <div class="start-menu-header">
        <div class="user-info">
          <v-avatar size="40" color="success">
            <span>{{ userInitials }}</span>
          </v-avatar>
          <div class="user-details">
            <div class="username">{{ username }}</div>
            <div class="user-role">{{ userRole }}</div>
          </div>
        </div>
      </div>
      <div class="start-menu-content">
        <div v-for="(group, index) in menuGroups" :key="index" class="menu-group">
          <div class="menu-group-header" @click="toggleGroup(group.id)">
            <v-icon :color="group.color">{{ group.icon }}</v-icon>
            <span>{{ group.label }}</span>
            <v-icon class="expand-icon" :class="{ 'rotated': expandedGroups.includes(group.id) }">
              mdi-chevron-right
            </v-icon>
          </div>
          <div v-if="expandedGroups.includes(group.id)" class="menu-group-items">
            <div v-for="item in group.items" :key="item.id"
                 class="start-menu-item"
                 @click="openWindow(item)">
              <v-icon :color="item.color">{{ item.icon }}</v-icon>
              <span>{{ item.label }}</span>
            </div>
          </div>
        </div>
      </div>
      <div class="start-menu-footer">
        <div class="start-menu-item" @click="toggleDesktopMode">
          <v-icon>mdi-monitor</v-icon>
          <span>Zurück zum Normalmodus</span>
        </div>
        <div class="start-menu-item" @click="logout">
          <v-icon color="error">mdi-logout</v-icon>
          <span>Abmelden</span>
        </div>
      </div>
    </div>

    <!-- Password Dialog -->
    <v-dialog v-model="showPasswordDialog" max-width="400" persistent>
      <v-card>
        <v-card-title class="text-h5">
          Passwort erforderlich
        </v-card-title>
        <v-card-text>
          <v-text-field
            v-model="password"
            label="Passwort"
            type="password"
            :error="passwordError"
            :error-messages="passwordError ? 'Falsches Passwort' : ''"
            @keydown.enter.prevent="checkPassword"
            autofocus
          ></v-text-field>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="error" variant="text" @click="showPasswordDialog = false">
            Abbrechen
          </v-btn>
          <v-btn color="success" variant="text" @click="checkPassword">
            Bestätigen
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount, defineAsyncComponent, h, markRaw, watch } from 'vue';
import { useAuthStore } from '@/stores/auth';
import { useRouter, useRoute } from 'vue-router';
import axios from 'axios';
import { API_BASE_URL } from '@/config';

const router = useRouter();
const authStore = useAuthStore();
const props = defineProps({
  desktopMode: {
    type: Boolean,
    required: true
  }
});

const emit = defineEmits(['update:desktopMode']);

const isDesktopMode = computed({
  get: () => props.desktopMode,
  set: (value) => emit('update:desktopMode', value)
});

const windows = ref([]);
const showStartMenu = ref(false);
const currentTime = ref('');
const showDate = ref(false);
const showCalendar = ref(false);
const selectedDate = ref(new Date().toISOString().substr(0, 10));
const expandedGroups = ref([]);
const showPasswordDialog = ref(false);
const password = ref('');
const passwordError = ref(false);
const isPasswordVerified = ref(false);
let timeInterval;

const draggedIcon = ref(null);
const gridSize = 100; // Erhöhte Grid-Größe für bessere Sichtbarkeit

// Add restore functionality to recycle bin
const recycleBin = ref({
  id: 'recyclebin',
  label: 'Papierkorb',
  icon: 'mdi-delete',
  color: 'error',
  position: { x: 0, y: 0 },
  items: new Set() // Verwende Set statt Array für eindeutige Einträge
});
const iconPositions = ref({});

const DashboardView = defineAsyncComponent(() => import('@/views/DashboardView.vue'));
const AdminView = defineAsyncComponent(() => import('@/views/AdminView.vue'));
const ChatView = defineAsyncComponent(() => import('@/views/ChatView.vue'));
const CalendarView = defineAsyncComponent(() => import('@/views/CalendarView.vue'));
const InstandsetzungView = defineAsyncComponent(() => import('@/views/InstandsetzungView.vue'));
const RetourenView = defineAsyncComponent(() => import('@/views/RetourenView.vue'));
const NebentaetigkeitenView = defineAsyncComponent(() => import('@/views/NebentaetigkeitenView.vue'));
const ErsatzteileView = defineAsyncComponent(() => import('@/views/SparePartsView.vue'));
const IrisCodeMatrixView = defineAsyncComponent(() => import('@/views/IrisCodeMatrixView.vue'));
const DtcView = defineAsyncComponent(() => import('@/views/DtcView.vue'));
const BWareView = defineAsyncComponent(() => import('@/views/BWareView.vue'));
const BWareLeistungView = defineAsyncComponent(() => import('@/views/BWareLeistungView.vue'));
const CommissionView = defineAsyncComponent(() => import('@/views/CommissionView.vue'));
const RequestView = defineAsyncComponent(() => import('@/views/RequestView.vue'));
const StatistikView = defineAsyncComponent(() => import('@/views/StatistikView.vue'));
const ManagementView = defineAsyncComponent(() => import('@/views/ManagementView.vue'));
const DevToolsView = defineAsyncComponent(() => import('@/views/DevToolsView.vue'));
const ReinigungsplanView = defineAsyncComponent(() => import('@/views/ReinigungsplanView.vue'));
const ZusammenfassungenView = defineAsyncComponent(() => import('@/views/SummaryView.vue'));
const CalculatorView = defineAsyncComponent(() => import('@/views/CalculatorView.vue'));
const QRCodeView = defineAsyncComponent(() => import('@/components/QRCodeGenerator.vue'));

const componentMap = {
  dashboard: markRaw(DashboardView),
  admin: markRaw(AdminView),
  chat: markRaw(ChatView),
  calendar: markRaw(CalendarView),
  instandsetzung: markRaw(InstandsetzungView),
  retouren: markRaw(RetourenView),
  nebentaetigkeiten: markRaw(NebentaetigkeitenView),
  ersatzteile: markRaw(ErsatzteileView),
  'iris-code-matrix': markRaw(IrisCodeMatrixView),
  dtc: markRaw(DtcView),
  'b-ware': markRaw(BWareView),
  'b-ware-leistung': markRaw(BWareLeistungView),
  commission: markRaw(CommissionView),
  request: markRaw(RequestView),
  statistik: markRaw(StatistikView),
  management: markRaw(ManagementView),
  devtools: markRaw(DevToolsView),
  reinigungsplan: markRaw(ReinigungsplanView),
  zusammenfassungen: markRaw(ZusammenfassungenView),
  calculator: markRaw(CalculatorView),
  qrcode: markRaw(QRCodeView)
};

const desktopItems = computed(() => {
  const items = [];
  const user = authStore.user;

  // Dashboard
  if (user?.admin || user?.super_admin || user?.developer || user?.monteur || user?.returen_sorting) {
    items.push({
      id: 'dashboard',
      label: 'Dashboard',
      icon: 'mdi-view-dashboard',
      color: 'success',
      component: 'dashboard',
      route: '/dashboard'
    });
  }

  // Admin Bereich
  if (user?.admin || user?.super_admin || user?.developer) {
    items.push(
      {
        id: 'admin',
        label: 'Admin-Bereich',
        icon: 'mdi-shield-crown',
        color: 'success',
        component: 'admin',
        route: '/admin'
      },
      {
        id: 'reinigungsplan',
        label: 'Reinigungsplan',
        icon: 'mdi-broom',
        color: 'success',
        component: 'reinigungsplan',
        route: '/reinigungsplan'
      },
      {
        id: 'qrcode',
        label: 'QR-Code Generator',
        icon: 'mdi-qrcode',
        color: 'success',
        component: 'qrcode',
        route: '/qrcode'
      }
    );
  }

  // Management (nur Super Admin und Developer)
  if (user?.super_admin || user?.developer) {
    items.push({
      id: 'management',
      label: 'Management',
      icon: 'mdi-cogs',
      color: 'success',
      component: 'management',
      route: '/management'
    });
  }

  // Dev Tools (nur Developer)
  if (user?.developer) {
    items.push({
      id: 'devtools',
      label: 'Dev Tools',
      icon: 'mdi-alert',
      color: 'success',
      component: 'devtools',
      route: '/devtools'
    });
  }

  // Kommunikation
  if (user?.admin || user?.super_admin || user?.developer || user?.monteur || user?.returen_sorting) {
    items.push(
      {
        id: 'chat',
        label: 'Chat',
        icon: 'mdi-message',
        color: 'success',
        component: 'chat',
        route: '/chat'
      },
      {
        id: 'calendar',
        label: 'Kalender',
        icon: 'mdi-calendar',
        color: 'success',
        component: 'calendar',
        route: '/calendar'
      }
    );
  }

  // Werkstatt
  if (user?.admin || user?.super_admin || user?.developer || user?.monteur || user?.returen_sorting || user?.dtc) {
    if (user?.admin || user?.super_admin || user?.developer || user?.monteur || user?.returen_sorting) {
      items.push(
        {
          id: 'instandsetzung',
          label: 'Instandsetzung',
          icon: 'mdi-wrench-outline',
          color: 'success',
          component: 'instandsetzung',
          route: '/instandsetzung'
        },
        {
          id: 'retouren',
          label: 'Retouren',
          icon: 'mdi-recycle',
          color: 'success',
          component: 'retouren',
          route: '/retouren'
        },
        {
          id: 'nebentaetigkeiten',
          label: 'Nebentätigkeiten',
          icon: 'mdi-check-circle-outline',
          color: 'success',
          component: 'nebentaetigkeiten',
          route: '/nebentaetigkeiten'
        },
        {
          id: 'ersatzteile',
          label: 'Ersatzteile',
          icon: 'mdi-barcode',
          color: 'success',
          component: 'ersatzteile',
          route: '/ersatzteile'
        },
        {
          id: 'iris-code-matrix',
          label: 'IRIS Code Matrix',
          icon: 'mdi-table-search',
          color: 'success',
          component: 'iris-code-matrix',
          route: '/iris-code-matrix'
        },
        {
          id: 'b-ware',
          label: 'B-Ware',
          icon: 'mdi-quality-medium',
          color: 'amber',
          component: 'b-ware',
          route: '/b-ware'
        },
        {
          id: 'b-ware-leistung',
          label: 'B-Ware Leistung',
          icon: 'mdi-counter',
          color: 'amber',
          component: 'b-ware-leistung',
          route: '/b-ware-leistung'
        }
      );
    }
    if (user?.dtc || user?.admin || user?.super_admin || user?.developer) {
      items.push({
        id: 'dtc',
        label: 'DTC Retouren',
        icon: 'mdi-package-variant-closed',
        color: 'teal',
        component: 'dtc',
        route: '/dtc'
      });
    }
  }

  // Lager
  if (user?.admin || user?.super_admin || user?.developer || user?.lagerist || user?.monteur || user?.returen_sorting) {
    items.push(
      {
        id: 'commission',
        label: 'Kommissionen',
        icon: 'mdi-package-variant-closed',
        color: 'success',
        component: 'commission',
        route: '/commission'
      },
      {
        id: 'request',
        label: 'Anfrage',
        icon: 'mdi-help-circle-outline',
        color: 'success',
        component: 'request',
        route: '/request'
      }
    );
  }

  // Auswertung
  if (user?.admin || user?.super_admin || user?.developer || user?.monteur || user?.returen_sorting) {
    items.push(
      {
        id: 'statistik',
        label: 'Statistik',
        icon: 'mdi-chart-box-outline',
        color: 'success',
        component: 'statistik',
        route: '/statistik'
      },
      {
        id: 'zusammenfassungen',
        label: 'Zusammenfassungen',
        icon: 'mdi-text-box-outline',
        color: 'success',
        component: 'zusammenfassungen',
        route: '/summary'
      }
    );
  }

  // Taschenrechner (für alle Benutzer)
  items.push({
    id: 'calculator',
    label: 'Taschenrechner',
    icon: 'mdi-calculator',
    color: 'success',
    component: 'calculator',
    route: '/calculator'
  });

  return items;
});

const menuGroups = computed(() => {
  const groups = [];
  const user = authStore.user;

  // Hauptfunktionen
  if (user?.admin || user?.super_admin || user?.developer || user?.monteur || user?.returen_sorting) {
    groups.push({
      id: 'main',
      label: 'Hauptfunktionen',
      icon: 'mdi-apps',
      color: 'success',
      items: [
        {
          id: 'dashboard',
          label: 'Dashboard',
          icon: 'mdi-view-dashboard',
          color: 'success',
          component: 'dashboard',
          route: '/dashboard'
        },
        {
          id: 'chat',
          label: 'Chat',
          icon: 'mdi-message',
          color: 'success',
          component: 'chat',
          route: '/chat'
        },
        {
          id: 'calendar',
          label: 'Kalender',
          icon: 'mdi-calendar',
          color: 'success',
          component: 'calendar',
          route: '/calendar'
        },
        {
          id: 'calculator',
          label: 'Taschenrechner',
          icon: 'mdi-calculator',
          color: 'success',
          component: 'calculator',
          route: '/calculator'
        }
      ]
    });
  }

  // Admin-Bereich
  if (user?.admin || user?.super_admin || user?.developer) {
    groups.push({
      id: 'admin',
      label: 'Administration',
      icon: 'mdi-shield-crown',
      color: 'success',
      items: [
        {
          id: 'admin',
          label: 'Admin-Bereich',
          icon: 'mdi-shield-crown',
          color: 'success',
          component: 'admin',
          route: '/admin'
        },
        {
          id: 'reinigungsplan',
          label: 'Reinigungsplan',
          icon: 'mdi-broom',
          color: 'success',
          component: 'reinigungsplan',
          route: '/reinigungsplan'
        },
        {
          id: 'qrcode',
          label: 'QR-Code Generator',
          icon: 'mdi-qrcode',
          color: 'success',
          component: 'qrcode',
          route: '/qrcode'
        }
      ]
    });
  }

  // Management (nur Super Admin und Developer)
  if (user?.super_admin || user?.developer) {
    groups.push({
      id: 'management',
      label: 'Management',
      icon: 'mdi-cogs',
      color: 'success',
      items: [
        {
          id: 'management',
          label: 'Management',
          icon: 'mdi-cogs',
          color: 'success',
          component: 'management',
          route: '/management'
        }
      ]
    });
  }

  // Dev Tools (nur Developer)
  if (user?.developer) {
    groups.push({
      id: 'devtools',
      label: 'Dev Tools',
      icon: 'mdi-alert',
      color: 'success',
      items: [
        {
          id: 'devtools',
          label: 'Dev Tools',
          icon: 'mdi-alert',
          color: 'success',
          component: 'devtools',
          route: '/devtools'
        }
      ]
    });
  }

  // Werkstatt
  if (user?.admin || user?.super_admin || user?.developer || user?.monteur || user?.returen_sorting || user?.dtc) {
    const werkstattItems = [];
    if (user?.admin || user?.super_admin || user?.developer || user?.monteur || user?.returen_sorting) {
      werkstattItems.push(
        {
          id: 'instandsetzung',
          label: 'Instandsetzung',
          icon: 'mdi-wrench-outline',
          color: 'success',
          component: 'instandsetzung',
          route: '/instandsetzung'
        },
        {
          id: 'retouren',
          label: 'Retouren',
          icon: 'mdi-recycle',
          color: 'success',
          component: 'retouren',
          route: '/retouren'
        },
        {
          id: 'nebentaetigkeiten',
          label: 'Nebentätigkeiten',
          icon: 'mdi-check-circle-outline',
          color: 'success',
          component: 'nebentaetigkeiten',
          route: '/nebentaetigkeiten'
        }
      );
    }
    if (user?.dtc || user?.admin || user?.super_admin || user?.developer) {
      werkstattItems.push({
        id: 'dtc',
        label: 'DTC Retouren',
        icon: 'mdi-package-variant-closed',
        color: 'teal',
        component: 'dtc',
        route: '/dtc'
      });
    }
    if (user?.admin || user?.super_admin || user?.developer || user?.monteur) {
      werkstattItems.push(
        {
          id: 'b-ware',
          label: 'B-Ware',
          icon: 'mdi-quality-medium',
          color: 'amber',
          component: 'b-ware',
          route: '/b-ware'
        },
        {
          id: 'b-ware-leistung',
          label: 'B-Ware Leistung',
          icon: 'mdi-counter',
          color: 'amber',
          component: 'b-ware-leistung',
          route: '/b-ware-leistung'
        }
      );
    }
    if (werkstattItems.length > 0) {
      groups.push({
        id: 'werkstatt',
        label: 'Werkstatt',
        icon: 'mdi-tools',
        color: 'success',
        items: werkstattItems
      });
    }
  }

  // Lager
  if (user?.admin || user?.super_admin || user?.developer || user?.lagerist || user?.monteur || user?.returen_sorting) {
    groups.push({
      id: 'lager',
      label: 'Lager',
      icon: 'mdi-package-variant',
      color: 'success',
      items: [
        {
          id: 'commission',
          label: 'Kommissionen',
          icon: 'mdi-package-variant-closed',
          color: 'success',
          component: 'commission',
          route: '/commission'
        },
        {
          id: 'request',
          label: 'Anfrage',
          icon: 'mdi-help-circle-outline',
          color: 'success',
          component: 'request',
          route: '/request'
        }
      ]
    });
  }

  // Auswertung
  if (user?.admin || user?.super_admin || user?.developer || user?.monteur || user?.returen_sorting) {
    groups.push({
      id: 'auswertung',
      label: 'Auswertung',
      icon: 'mdi-chart-box',
      color: 'success',
      items: [
        {
          id: 'statistik',
          label: 'Statistik',
          icon: 'mdi-chart-box-outline',
          color: 'success',
          component: 'statistik',
          route: '/statistik'
        },
        {
          id: 'zusammenfassungen',
          label: 'Zusammenfassungen',
          icon: 'mdi-text-box-outline',
          color: 'success',
          component: 'zusammenfassungen',
          route: '/summary'
        }
      ]
    });
  }

  return groups;
});

const startMenuItems = computed(() => {
  return menuGroups.value.flatMap(group => group.items);
});

const userInitials = computed(() => {
  const user = authStore.user;
  return user ? `${user.vorname?.[0] || ''}${user.nachname?.[0] || ''}` : '?';
});

const username = computed(() => {
  return authStore.user?.loginname || 'Gast';
});

const userRole = computed(() => {
  if (authStore.user?.super_admin) return 'Super Administrator';
  if (authStore.user?.admin) return 'Administrator';
  if (authStore.user?.developer) return 'Developer';
  if (authStore.user?.monteur) return 'Monteur';
  if (authStore.user?.lagerist) return 'Lagerist';
  if (authStore.user?.returen_sorting) return 'Retouren Sortierung';
  return 'Benutzer';
});

const currentDate = computed(() => {
  return new Date().toLocaleDateString('de-DE', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
});

const updateTime = () => {
  const now = new Date();
  currentTime.value = now.toLocaleTimeString('de-DE', {
    hour: '2-digit',
    minute: '2-digit'
  });
};

const openWindow = (item) => {
  if (item.id === 'zusammenfassungen' && !isPasswordVerified.value) {
    promptPassword();
    return;
  }

  const existingWindow = windows.value.find(w => w.id === item.id);
  if (existingWindow) {
    if (existingWindow.isMinimized) {
      existingWindow.isMinimized = false;
    }
    bringToFront(existingWindow.id);
    return;
  }

  // Calculate random position within visible area
  const maxWidth = window.innerWidth - 800;
  const maxHeight = window.innerHeight - 640;
  const left = Math.max(0, Math.floor(Math.random() * maxWidth));
  const top = Math.max(0, Math.floor(Math.random() * maxHeight));

  const newWindow = {
    id: item.id,
    title: item.label,
    icon: item.icon,
    iconColor: item.color,
    route: item.route,
    isActive: true,
    isMinimized: false,
    isMaximized: false,
    isDragging: false,
    isResizing: false,
    style: {
      left: `${left}px`,
      top: `${top}px`,
      width: '800px',
      height: '600px',
      zIndex: windows.value.length + 1
    }
  };

  windows.value.push(newWindow);
  showStartMenu.value = false;

  // Navigate to the route
  if (item.route && router.currentRoute.value.path !== item.route) {
    router.push(item.route);
  }
};

const closeWindow = (id) => {
  if (id === 'zusammenfassungen') {
    isPasswordVerified.value = false;
  }
  windows.value = windows.value.filter(w => w.id !== id);
};

const minimizeWindow = (id) => {
  const window = windows.value.find(w => w.id === id);
  if (window) {
    window.isMinimized = true;
    window.isActive = false;
  }
};

const maximizeWindow = (id) => {
  const window = windows.value.find(w => w.id === id);
  if (window) {
    window.isMaximized = !window.isMaximized;
    if (window.isMaximized) {
      window.style = {
        ...window.style,
        left: '0',
        top: '0',
        width: '100%',
        height: 'calc(100% - 40px)'
      };
    } else {
      window.style = {
        ...window.style,
        left: '100px',
        top: '100px',
        width: '800px',
        height: '600px'
      };
    }
  }
};

const restoreWindow = (id) => {
  const window = windows.value.find(w => w.id === id);
  if (window) {
    window.isMinimized = false;
    window.isActive = true;
    bringToFront(id);
  }
};

const bringToFront = (id) => {
  const maxZIndex = Math.max(...windows.value.map(w => w.style.zIndex));
  windows.value.forEach(w => {
    w.isActive = w.id === id;
    if (w.id === id) {
      w.style.zIndex = maxZIndex + 1;
    }
  });
};

const startIconDrag = (event, icon) => {
  draggedIcon.value = icon;
  event.dataTransfer.effectAllowed = 'move';
};

const startWindowDrag = (event, windowId) => {
  event.preventDefault();
  const window = windows.value.find(w => w.id === windowId);
  if (!window || window.isMaximized) return;

  bringToFront(windowId);

  // Get the offset between mouse and window position
  const rect = event.target.closest('.window').getBoundingClientRect();
  const offsetX = event.clientX - rect.left;
  const offsetY = event.clientY - rect.top;

  const viewportWidth = document.documentElement.clientWidth;
  const viewportHeight = document.documentElement.clientHeight;
  const windowWidth = rect.width;
  const windowHeight = rect.height;

  const onMouseMove = (e) => {
    // Calculate new position based on mouse position minus the offset
    const newLeft = Math.max(0, Math.min(e.clientX - offsetX, viewportWidth - windowWidth));
    const newTop = Math.max(0, Math.min(e.clientY - offsetY, viewportHeight - windowHeight));

    window.style.left = `${newLeft}px`;
    window.style.top = `${newTop}px`;
  };

  const onMouseUp = () => {
    document.removeEventListener('mousemove', onMouseMove);
    document.removeEventListener('mouseup', onMouseUp);
  };

  document.addEventListener('mousemove', onMouseMove);
  document.addEventListener('mouseup', onMouseUp);
};

const onDragOver = (event) => {
  event.preventDefault();
  event.dataTransfer.dropEffect = 'move';

  // Füge drag-over Effekt zum Papierkorb hinzu
  const recycleBin = document.querySelector('.recycle-bin');
  const recycleBinRect = recycleBin.getBoundingClientRect();

  if (
    event.clientX >= recycleBinRect.left &&
    event.clientX <= recycleBinRect.right &&
    event.clientY >= recycleBinRect.top &&
    event.clientY <= recycleBinRect.bottom
  ) {
    recycleBin.classList.add('drag-over');
  } else {
    recycleBin.classList.remove('drag-over');
  }
};

const onDrop = async (event) => {
  event.preventDefault();
  if (!draggedIcon.value) return;

  const rect = event.currentTarget.getBoundingClientRect();
  const x = Math.round((event.clientX - rect.left) / gridSize) * gridSize;
  const y = Math.round((event.clientY - rect.top) / gridSize) * gridSize;

  // Prüfe, ob das Icon über dem Papierkorb abgelegt wurde
  const recycleBinRect = document.querySelector('.recycle-bin').getBoundingClientRect();
  if (
    event.clientX >= recycleBinRect.left &&
    event.clientX <= recycleBinRect.right &&
    event.clientY >= recycleBinRect.top &&
    event.clientY <= recycleBinRect.bottom
  ) {
    // Icon in den Papierkorb legen
    recycleBin.value.items.add(draggedIcon.value.id);
    iconPositions.value[draggedIcon.value.id] = {
      ...iconPositions.value[draggedIcon.value.id],
      is_visible: false
    };
  } else {
    // Stelle sicher, dass die Position innerhalb des sichtbaren Bereichs ist
    const maxX = rect.width - gridSize;
    const maxY = rect.height - gridSize;
    const newX = Math.max(0, Math.min(x, maxX));
    const newY = Math.max(0, Math.min(y, maxY));

    // Icon an neuer Position platzieren
    iconPositions.value[draggedIcon.value.id] = {
      x: newX,
      y: newY,
      is_visible: true
    };
    // Entferne Icon aus dem Papierkorb, falls es dort war
    recycleBin.value.items.delete(draggedIcon.value.id);
  }

  await saveIconPositions();
  draggedIcon.value = null;
  document.querySelector('.recycle-bin').classList.remove('drag-over');
};

const saveIconPositions = async () => {
  try {
    const endpoint = `${API_BASE_URL.replace(/\/+$/, '')}/desktop_icons.php`;
    const icons = Object.entries(iconPositions.value).map(([id, position]) => ({
      id: id,
      x: position.x,
      y: position.y,
      visible: position.is_visible ? 1 : 0
    }));

    await axios.post(endpoint, {
      action: 'save',
      icons: icons
    }, {
      withCredentials: true,
      headers: {
        'Content-Type': 'application/json'
      }
    });
  } catch (error) {
    if (error.response?.status === 401) {
      router.push('/login');
    }
    console.error('Failed to save icon positions:', error);
  }
};

const loadIconPositions = async () => {
  try {
    // Initialize with default layout first
    iconPositions.value = { ...DEFAULT_LAYOUT };

    // Try to load saved positions
    const endpoint = `${API_BASE_URL.replace(/\/+$/, '')}/desktop_icons.php?action=get`;
    const response = await axios.get(endpoint, {
      withCredentials: true,
      headers: {
        'Content-Type': 'application/json'
      }
    });

    // Reset recycle bin
    recycleBin.value.items.clear();

    // Only update positions if we have valid data
    if (response?.data?.success && Array.isArray(response.data.icons)) {
      response.data.icons.forEach(icon => {
        if (icon && typeof icon === 'object') {
          iconPositions.value[icon.icon_id] = {
            x: icon.position_x || 0,
            y: icon.position_y || 0,
            is_visible: icon.is_visible === 1 // Only set to false if explicitly 1
          };

          // Add to recycle bin if not visible
          if (!iconPositions.value[icon.icon_id].is_visible) {
            recycleBin.value.items.add(icon.icon_id);
          }
        }
      });
    }

    // Ensure all desktop items have a valid position and are visible by default
    desktopItems.value.forEach(item => {
      if (!iconPositions.value[item.id] || typeof iconPositions.value[item.id] !== 'object') {
        iconPositions.value[item.id] = {
          x: DEFAULT_LAYOUT[item.id]?.x || 0,
          y: DEFAULT_LAYOUT[item.id]?.y || 0,
          is_visible: true // Set to true by default
        };
      } else {
        // Ensure existing icons are visible by default
        iconPositions.value[item.id].is_visible = true;
      }
    });

  } catch (error) {
    console.error('Failed to load icon positions:', error);
    if (error.response?.status === 401) {
      router.push('/login');
    }
  }
};

const toggleStartMenu = () => {
  showStartMenu.value = !showStartMenu.value;
};

const toggleWindow = (id) => {
  const window = windows.value.find(w => w.id === id);
  if (window) {
    if (window.isMinimized) {
      restoreWindow(id);
    } else {
      minimizeWindow(id);
    }
  }
};

const toggleCalendar = () => {
  showCalendar.value = !showCalendar.value;
};

const toggleGroup = (groupId) => {
  const index = expandedGroups.value.indexOf(groupId);
  if (index === -1) {
    expandedGroups.value.push(groupId);
  } else {
    expandedGroups.value.splice(index, 1);
  }
};

const startResize = (event, windowId, direction) => {
  event.preventDefault();
  const window = windows.value.find(w => w.id === windowId);
  if (!window || window.isMaximized) return;

  const rect = event.target.closest('.window').getBoundingClientRect();
  const startX = event.clientX;
  const startY = event.clientY;
  const startWidth = rect.width;
  const startHeight = rect.height;

  const minWidth = 400;
  const minHeight = 300;
  const viewportWidth = document.documentElement.clientWidth;
  const viewportHeight = document.documentElement.clientHeight;

  const onMouseMove = (e) => {
    if (direction === 'right' || direction === 'corner') {
      const newWidth = Math.max(minWidth, Math.min(startWidth + (e.clientX - startX), viewportWidth - rect.left));
      window.style.width = `${newWidth}px`;
    }
    if (direction === 'bottom' || direction === 'corner') {
      const newHeight = Math.max(minHeight, Math.min(startHeight + (e.clientY - startY), viewportHeight - rect.top));
      window.style.height = `${newHeight}px`;
    }
  };

  const onMouseUp = () => {
    document.removeEventListener('mousemove', onMouseMove);
    document.removeEventListener('mouseup', onMouseUp);
  };

  document.addEventListener('mousemove', onMouseMove);
  document.addEventListener('mouseup', onMouseUp);
};

const promptPassword = () => {
  showPasswordDialog.value = true;
  password.value = '';
  passwordError.value = false;
};

const checkPassword = async () => {
  try {
    const response = await axios.post(
      `${API_BASE_URL}verify_password.php`,
      { action: "verify", password: password.value },
      { withCredentials: true }
    );

    if (response.data.status === "success") {
      isPasswordVerified.value = true;
      showPasswordDialog.value = false;
      password.value = '';
      passwordError.value = false;

      // Öffne das Fenster direkt
      openWindow({
        id: 'zusammenfassungen',
        label: 'Zusammenfassungen',
        icon: 'mdi-text-box-outline',
        color: 'success',
        component: 'zusammenfassungen',
        route: '/summary'
      });
    } else {
      passwordError.value = true;
    }
  } catch (error) {
    console.error('Fehler bei der Passwortprüfung:', error);
    if (axios.isAxiosError(error)) {
      console.error('Axios Fehler:', {
        status: error.response?.status,
        data: error.response?.data,
        headers: error.response?.headers
      });
    }
    passwordError.value = true;
  }
};

const visibleDesktopItems = computed(() => {
  return desktopItems.value.filter(item =>
    iconPositions.value[item.id]?.is_visible !== false
  );
});

const getIconColor = (iconId) => {
  return desktopItems.value.find(item => item.id === iconId)?.color || 'success';
};

const getIconName = (iconId) => {
  return desktopItems.value.find(item => item.id === iconId)?.icon || 'mdi-help';
};

const getIconLabel = (iconId) => {
  return desktopItems.value.find(item => item.id === iconId)?.label || 'Unbekannt';
};

const restoreIcon = (iconId) => {
  if (iconPositions.value[iconId]) {
    // Setze Icon auf Standardposition, falls keine Position gespeichert ist
    if (!iconPositions.value[iconId].x && !iconPositions.value[iconId].y) {
      iconPositions.value[iconId] = {
        x: DEFAULT_LAYOUT[iconId]?.x || 0,
        y: DEFAULT_LAYOUT[iconId]?.y || 0,
        is_visible: true
      };
    } else {
      iconPositions.value[iconId].is_visible = true;
    }
    recycleBin.value.items.delete(iconId);
    saveIconPositions();
  }
};

const logout = async () => {
  try {
    await axios.post(`${API_BASE_URL.replace(/\/+$/, '')}/logout.php`, { action: "logout" }, {
      withCredentials: true
    });
    authStore.logout();
    router.push('/login');
  } catch (error) {
    console.error('Logout failed:', error);
  }
};

onMounted(() => {
  updateTime();
  timeInterval = setInterval(updateTime, 1000);
  loadIconPositions();
});

onBeforeUnmount(() => {
  clearInterval(timeInterval);
});

// Add default layout constant
const DEFAULT_LAYOUT = {
  dashboard: { x: 0, y: 0 },
  admin: { x: 0, y: 100 },
  reinigungsplan: { x: 0, y: 200 },
  management: { x: 0, y: 300 },
  devtools: { x: 0, y: 400 },

  chat: { x: 100, y: 0 },
  calendar: { x: 100, y: 100 },
  calculator: { x: 100, y: 200 },

  instandsetzung: { x: 200, y: 0 },
  retouren: { x: 200, y: 100 },
  nebentaetigkeiten: { x: 200, y: 200 },
  ersatzteile: { x: 200, y: 300 },
  'iris-code-matrix': { x: 200, y: 400 },
  'b-ware': { x: 200, y: 500 },
  'b-ware-leistung': { x: 200, y: 600 },

  commission: { x: 300, y: 0 },
  request: { x: 300, y: 100 },

  statistik: { x: 400, y: 0 },
  zusammenfassungen: { x: 400, y: 100 }
};

const toggleDesktopMode = () => {
  isDesktopMode.value = false;
  // Navigate to dashboard when leaving desktop mode
  router.push('/dashboard');
};

// Update watch for isDesktopMode
watch(() => props.desktopMode, async (newValue) => {
  if (newValue) {
    await loadIconPositions();
  } else {
    await saveIconPositions();
    // Navigate to dashboard when desktop mode is disabled
    router.push('/dashboard');
  }
});
</script>

<style scoped>
.desktop-container {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: #121212;
  overflow: hidden;
  z-index: 1000; /* Ensure desktop view is above normal mode */
}

.desktop-icons {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 40px;
  overflow: hidden;
}

.desktop-icon {
  position: absolute;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  cursor: move;
  padding: 8px;
  border-radius: 8px;
  transition: transform 0.2s ease, background-color 0.2s ease;
  width: 100px; /* Angepasst an gridSize */
  height: 100px; /* Angepasst an gridSize */
  user-select: none;
  background-color: transparent;
}

.desktop-icon:hover {
  background-color: rgba(255, 255, 255, 0.1);
}

.desktop-icon.dragging {
  opacity: 0.5;
  background-color: rgba(255, 255, 255, 0.2);
}

.recycle-bin {
  position: absolute;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 8px;
  border-radius: 8px;
  transition: all 0.2s ease;
  width: 80px;
  height: 80px;
  user-select: none;
  background-color: rgba(244, 67, 54, 0.1);
  border: 1px dashed rgba(244, 67, 54, 0.5);
  right: 20px;
  bottom: 60px;
}

.recycle-bin:hover {
  background-color: rgba(244, 67, 54, 0.2);
}

.recycle-bin.drag-over {
  background-color: rgba(244, 67, 54, 0.3);
  border: 1px solid rgba(244, 67, 54, 0.8);
  transform: scale(1.1);
}

.icon-label {
  margin-top: 4px;
  text-align: center;
  color: white;
  font-size: 12px;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  pointer-events: none;
}

.window {
  position: absolute;
  background: rgba(30, 30, 40, 0.95);
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  will-change: transform;
}

.window-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 12px;
  background: rgba(40, 40, 50, 0.95);
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  cursor: move;
  user-select: none;
  height: 40px;
}

.window-title {
  display: flex;
  align-items: center;
  color: white;
  font-size: 14px;
  font-weight: 500;
  gap: 8px;
}

.window-controls {
  display: flex;
  gap: 4px;
}

.window-controls .v-btn {
  width: 24px;
  height: 24px;
  border-radius: 4px;
}

.window-controls .v-btn:hover {
  background-color: rgba(255, 255, 255, 0.1);
}

.window-controls .v-btn:last-child:hover {
  background-color: rgba(244, 67, 54, 0.1);
}

.window-content {
  height: calc(100% - 40px);
  overflow: hidden;
  background: rgba(25, 25, 30, 0.95);
  position: relative;
  padding: 16px;
  pointer-events: auto;
}

.resize-handle {
  position: absolute;
  background: transparent;
}

.resize-right {
  right: 0;
  top: 0;
  width: 4px;
  height: 100%;
  cursor: e-resize;
}

.resize-bottom {
  bottom: 0;
  left: 0;
  width: 100%;
  height: 4px;
  cursor: s-resize;
}

.resize-corner {
  right: 0;
  bottom: 0;
  width: 12px;
  height: 12px;
  cursor: se-resize;
}

.window.minimized {
  transform: scale(0.1) !important;
  opacity: 0;
  pointer-events: none;
  transition: all 0.3s ease;
}

/* Remove transition during drag/resize */
.window.dragging,
.window.resizing {
  transition: none !important;
}

.taskbar {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  height: 40px;
  background: rgba(30, 30, 40, 0.95);
  display: flex;
  align-items: center;
  padding: 0 8px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  z-index: 1000;
}

.start-menu {
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  border-radius: 4px;
  transition: background-color 0.2s;
}

.start-menu:hover {
  background-color: rgba(255, 255, 255, 0.1);
}

.taskbar-items {
  display: flex;
  gap: 4px;
  margin: 0 8px;
  flex: 1;
}

.taskbar-item {
  height: 32px;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 12px;
  cursor: pointer;
  border-radius: 4px;
  transition: all 0.2s ease;
  margin-top: 4px;
}

.taskbar-item:hover {
  background-color: rgba(255, 255, 255, 0.1);
}

.taskbar-item.active {
  background-color: rgba(76, 175, 80, 0.2);
  border-bottom: 2px solid var(--v-theme-success);
}

.taskbar-label {
  color: white;
  font-size: 12px;
  max-width: 120px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.system-tray {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 8px;
}

.tray-item {
  display: flex;
  align-items: center;
  padding: 4px 8px;
  border-radius: 4px;
  color: white;
  font-size: 12px;
}

.time {
  font-family: monospace;
  min-width: 50px;
  text-align: center;
}

.start-menu-panel {
  position: fixed;
  bottom: 40px;
  left: 0;
  width: 300px;
  max-height: calc(100vh - 60px);
  background: rgba(30, 30, 40, 0.98);
  border-radius: 8px 8px 0 0;
  box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.3);
  display: flex;
  flex-direction: column;
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.start-menu-header {
  padding: 16px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  background: linear-gradient(to bottom, rgba(76, 175, 80, 0.1), transparent);
}

.user-info {
  display: flex;
  align-items: center;
  gap: 12px;
}

.user-details {
  display: flex;
  flex-direction: column;
}

.username {
  color: white;
  font-weight: 500;
}

.user-role {
  color: rgba(255, 255, 255, 0.7);
  font-size: 12px;
}

.start-menu-content {
  padding: 8px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  overflow-y: auto;
}

.start-menu-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 12px;
  border-radius: 4px;
  cursor: pointer;
  color: white;
  transition: all 0.2s ease;
}

.start-menu-item:hover {
  background-color: rgba(255, 255, 255, 0.1);
  transform: translateX(4px);
}

.window-component {
  width: 100%;
  height: 100%;
  overflow: auto;
  position: relative;
  z-index: 1;
  pointer-events: auto;
}

.recycle-bin-count {
  position: absolute;
  top: -8px;
  right: -8px;
  background-color: var(--v-theme-error);
  color: white;
  border-radius: 50%;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  cursor: pointer;
}

.recycle-bin-menu {
  max-height: 300px;
  overflow-y: auto;
  background: rgba(30, 30, 40, 0.98);
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.recycle-bin-menu .v-list {
  background: transparent;
}

.recycle-bin-item {
  cursor: pointer;
  transition: all 0.2s ease;
}

.recycle-bin-item:hover {
  background-color: rgba(255, 255, 255, 0.1);
  transform: translateX(4px);
}

.recycle-bin-item .v-icon {
  margin-right: 8px;
}

/* Ensure window controls are always clickable */
.window-controls {
  z-index: 2;
  position: relative;
}

.window-header {
  z-index: 2;
  position: relative;
}

.user-menu {
  cursor: pointer;
  padding: 0 12px;
}

.user-menu:hover {
  background-color: rgba(255, 255, 255, 0.1);
}

.user-menu-card {
  background: rgba(30, 30, 40, 0.98);
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.user-menu-card .v-list {
  background: transparent;
}

.user-menu-card .v-list-item {
  cursor: pointer;
  transition: all 0.2s ease;
}

.user-menu-card .v-list-item:hover {
  background-color: rgba(255, 255, 255, 0.1);
  transform: translateX(4px);
}

.user-menu-card .v-list-item .v-icon {
  margin-right: 8px;
}

.start-menu-footer {
  margin-top: auto;
  padding: 8px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
}

.start-menu-footer .start-menu-item {
  margin-bottom: 4px;
}

.start-menu-footer .start-menu-item:last-child {
  margin-bottom: 0;
}
</style>
